import _typeof from "@babel/runtime/helpers/esm/typeof";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
import { getCrcCode } from '@nike/ux-tread-crc-code';
import { addPageAction } from '@nike/ux-tread-new-relic';
import { getApiPathFromUrl } from '@nike/ux-tread-dotcom-utils';
var reduceErrors = function reduceErrors(errorsArr, url, servicePath) {
  return errorsArr.reduce(function (prev, curr) {
    var _body$errors;
    var _ref = curr || {},
      body = _ref.body,
      errors = _ref.errors,
      restOfCurr = _objectWithoutProperties(_ref, ["body", "errors"]);
    if (errors !== null && errors !== void 0 && errors.length) {
      return reduceErrors([].concat(_toConsumableArray(prev), _toConsumableArray(errors)), url, restOfCurr === null || restOfCurr === void 0 ? void 0 : restOfCurr.servicePath);
    }
    var reducedErrors = [].concat(_toConsumableArray(prev), [_objectSpread(_objectSpread({}, restOfCurr), {}, {
      crcCode: (restOfCurr === null || restOfCurr === void 0 ? void 0 : restOfCurr.crcCode) || getCrcCode({
        url: url,
        code: restOfCurr === null || restOfCurr === void 0 ? void 0 : restOfCurr.code,
        status: restOfCurr === null || restOfCurr === void 0 ? void 0 : restOfCurr.status,
        message: restOfCurr === null || restOfCurr === void 0 ? void 0 : restOfCurr.message
      }),
      servicePath: (restOfCurr === null || restOfCurr === void 0 ? void 0 : restOfCurr.servicePath) || servicePath
    })]);
    if (body !== null && body !== void 0 && (_body$errors = body.errors) !== null && _body$errors !== void 0 && _body$errors.length) {
      return reduceErrors([].concat(_toConsumableArray(reducedErrors), _toConsumableArray((body === null || body === void 0 ? void 0 : body.errors) || [])), url, restOfCurr === null || restOfCurr === void 0 ? void 0 : restOfCurr.servicePath);
    }
    return reducedErrors.filter(function (err) {
      return err.servicePath;
    }); // we get dupes so we drop them
  }, []);
};

/**
 * @typedef {object} ApiError
 * @property {string} crcCode - a unique code generated per service path, code, or statusCode or message
 * @property {string} code - error code returned in nike standardized api error patterns
 * @property {string} message - error message returned in nike standardized api error patterns
 * @property {string} field - error field returned in nike standardized api error patterns
 * @property {string} message - error message returned in nike standardized api error patterns
 */

/**
 * interceptErrors uniformly tracks errors to newrelic via addPageAction and returns the normalized errors array
 * to return to clients
 *
 * @returns {Array<ApiError>} array of api errors to attach to a FetchResponseError
 */
export default function interceptErrors(_ref2) {
  var _data$error, _errorsWithCrcCodes$;
  var data = _ref2.data,
    response = _ref2.response,
    traceId = _ref2.traceId,
    url = _ref2.url,
    _ref2$pageActionName = _ref2.pageActionName,
    pageActionName = _ref2$pageActionName === void 0 ? 'fetch-client-pageAction' : _ref2$pageActionName;
  var dataErrors = data === null || data === void 0 ? void 0 : data.errors;
  var dataErrorErrors = data === null || data === void 0 ? void 0 : (_data$error = data.error) === null || _data$error === void 0 ? void 0 : _data$error.errors;
  var dataData = (data === null || data === void 0 ? void 0 : data.data) || {};
  var errorsArr = dataErrors || dataErrorErrors;
  var message = (response === null || response === void 0 ? void 0 : response.message) || (data === null || data === void 0 ? void 0 : data.message) || 'No message';
  var servicePath = getApiPathFromUrl(url) || 'Unknown';
  var normalizedErrorArr = [_objectSpread(_objectSpread({}, data), {}, {
    message: message,
    status: response === null || response === void 0 ? void 0 : response.status,
    statusText: response === null || response === void 0 ? void 0 : response.statusText,
    // adding a check for existing crcCode - should cover newer APIs but also grand crc codes
    crcCode: (data === null || data === void 0 ? void 0 : data.crcCode) || getCrcCode({
      url: url,
      code: data === null || data === void 0 ? void 0 : data.code,
      status: response === null || response === void 0 ? void 0 : response.status,
      message: message
    })
  })];
  var dataError = data === null || data === void 0 ? void 0 : data.error;
  var dataErrorWithCode = dataError && typeof dataError.code === 'string';
  // account for data from graphql responses where error could be inside data[key].error
  Object.keys(dataData).forEach(function (key) {
    var _dataData$key;
    // you might have data.fulfillmentOfferings.error or data.cartReviews.error
    if ((_dataData$key = dataData[key]) !== null && _dataData$key !== void 0 && _dataData$key.error) {
      normalizedErrorArr.push(dataData[key].error);
    }
  });
  if (errorsArr && errorsArr.length) {
    normalizedErrorArr = reduceErrors([].concat(_toConsumableArray(errorsArr), _toConsumableArray(normalizedErrorArr)), url, servicePath);
  } else if (dataErrorWithCode) {
    var error = data === null || data === void 0 ? void 0 : data.error;
    normalizedErrorArr = reduceErrors([_objectSpread(_objectSpread({}, error), {}, {
      crcCode: (error === null || error === void 0 ? void 0 : error.crcCode) || getCrcCode({
        url: url,
        code: error.code
      })
      // adding a check for existing crcCode - should cover newer APIs but also grand crc codes
    })], url, servicePath);
  }
  var errorsWithCrcCodes = normalizedErrorArr.filter(function (err) {
    return err.crcCode;
  });
  var finalErrorsArray = errorsWithCrcCodes.length === 1 && !(errorsWithCrcCodes !== null && errorsWithCrcCodes !== void 0 && (_errorsWithCrcCodes$ = errorsWithCrcCodes[0]) !== null && _errorsWithCrcCodes$ !== void 0 && _errorsWithCrcCodes$.servicePath) ? errorsWithCrcCodes.map(function (err) {
    return _objectSpread(_objectSpread({}, err), {}, {
      servicePath: servicePath
    });
  }) : errorsWithCrcCodes;
  var called = {};
  // for loops are much faster in some browsers so prefer forloop vs forEach
  for (var index = 0; index < finalErrorsArray.length; index += 1) {
    var _error = finalErrorsArray[index];
    if (_error && _typeof(_error) === 'object') {
      var finalServicePath = _error.servicePath || servicePath;
      var parentServicePath = _error.servicePath && _error.servicePath !== servicePath && servicePath;
      var eventData = _objectSpread(_objectSpread({
        traceId: traceId
      }, _error), {}, {
        servicePath: finalServicePath
      });
      if (parentServicePath) {
        eventData.parentServicePath = parentServicePath;
      }
      if (eventData !== null && eventData !== void 0 && eventData.crcCode && !called["".concat(eventData.crcCode).concat(eventData.servicePath)]) {
        // NOTE to avoid redundant pageActions from nested errors that might show up in 2 parts of the same response
        //  we keep track of the calls via in memory called object
        called["".concat(eventData.crcCode).concat(eventData.servicePath)] = true;
        addPageAction(pageActionName, eventData);
      }
    }
  }
  return finalErrorsArray;
}